<template>
  <section class="section" id="buy-now-pay-later-transactions-panel">
    <div class="container">
      <b-loading
        :is-full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></b-loading>

      <buy-now-pay-later-title @get-buy-now-pay-later="getBuyNowPayLater" />

      <b-tabs
        position="is-centered"
        class="block"
        size="is-large"
        expanded
        @input="changeTabsStyle"
      >
        <b-tab-item :label="$t('buyNowPayLater.transactions')">
          <h2
            v-if="Object.keys(bills).length === 0"
            class="subtitle has-text-centered"
            style="margin-top: 3rem"
          >
            {{ $t("buyNowPayLater.noBuyNowPayLaterTransactionsAtThisTime") }}
          </h2>

          <div v-else>
            <div v-for="(bill, i) in bills" :key="i">
              <div
                class="columns"
                style="margin-top: 1rem; margin-bottom: 1rem"
              >
                <div class="column">
                  <p>
                    <b>{{ $t("buyNowPayLater.store") }}:</b>
                    {{ bill.store.storeInfo.name }}
                  </p>

                  <p>
                    <b>{{ $t("buyNowPayLater.partner") }}:</b>
                    {{
                      `${bill.partner.companyInfo.nationalCompanyId} - ${bill.partner.companyInfo.companyName}`
                    }}
                  </p>

                  <p>
                    <b>{{ $t("buyNowPayLater.totalBilletValue") }}:</b>
                    {{ `R$${formatMoney(bill.totalBilletvalue)}` }}
                  </p>
                </div>

                <div class="column is-narrow">
                  <button
                    class="button"
                    :style="{
                      backgroundColor: $primaryColor,
                      color: $primaryColorContrast
                    }"
                    @click="confirmBilletGeneration(bill)"
                  >
                    {{ $t("buyNowPayLater.createBillet") }}
                  </button>
                </div>
              </div>

              <buy-now-pay-later-table :transactions="bill.transactions" />

              <hr class="separator" />
            </div>
          </div>
        </b-tab-item>

        <b-tab-item :label="$t('buyNowPayLater.billets')">
          <billets-table :billets="billetsReverse" />
        </b-tab-item>
      </b-tabs>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../mixins/common";
import { mask } from "../../mixins/mask";
import buyNowPayLaterTitle from "./BuyNowPayLaterTitle";
import buyNowPayLaterTable from "./BuyNowPayLaterTable";
import billetsTable from "./BilletsTable";

export default {
  mixins: [common, mask],

  components: {
    buyNowPayLaterTitle,
    buyNowPayLaterTable,
    billetsTable
  },

  data() {
    return {
      isLoading: false,
      stores: [],
      partners: [],
      bills: {}
    };
  },

  methods: {
    getBuyNowPayLater() {
      this.isLoading = true;
      this.$store
        .dispatch("getBuyNowPayLater")
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.stores = res.data.payload.stores;
            this.partners = res.data.payload.partners;
            this.mountBills();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmBilletGeneration(bill) {
      this.$buefy.dialog.confirm({
        message: this.$t("buyNowPayLater.confirmBilletGeneration"),
        cancelText: this.$t("common.cancel"),
        confirmText: this.$t("common.yes"),
        onConfirm: () => this.generateBillet(bill)
      });
    },

    generateBillet(bill) {
      this.isLoading = true;
      this.$store
        .dispatch("generateBillet", {
          storeId: bill.store._id,
          partnerId: bill.partner._id
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$buefy.toast.open({
              message: `${this.$t("buyNowPayLater.billetGenerated")}!`,
              type: "is-success",
              position: "is-bottom",
              duration: 3000
            });

            delete this.bills[`s-${bill.store._id}-p-${bill.partner._id}`];
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    changeTabsStyle(tabId) {
      const tabsQty = document.getElementsByClassName("tabs")[0].children[0]
        .children.length;

      let tab;
      for (let i = 0; i < tabsQty; i++) {
        tab = document.getElementsByClassName("tabs")[0].children[0].children[
          i
        ];

        if (tab.children[0].id === `${tabId}-label`) {
          tab.children[0].style.setProperty(
            "border-bottom-color",
            this.$primaryColor,
            "important"
          );
          tab.children[0].style.setProperty(
            "color",
            this.$primaryColor,
            "important"
          );
        } else {
          tab.children[0].style.setProperty(
            "border-bottom-color",
            "rgb(219, 219, 219)",
            "important"
          );
          tab.children[0].style.setProperty("color", "#4A4A4A", "important");
        }
      }
    },

    mountBills() {
      this.bills = {};
      for (let i = 0; i < this.buyNowPayLaterTransactionsReverse.length; i++) {
        if (
          !this.bills.hasOwnProperty(
            `s-${this.buyNowPayLaterTransactionsReverse[i].store._id}-p-${this.buyNowPayLaterTransactionsReverse[i].buyNowPayLater.liablePartner._id}`
          )
        ) {
          const storeIndex = this.stores.findIndex(s => {
            return (
              s._id === this.buyNowPayLaterTransactionsReverse[i].store._id
            );
          });

          const partnerIndex = this.partners.findIndex(p => {
            return (
              p._id ===
              this.buyNowPayLaterTransactionsReverse[i].buyNowPayLater
                .liablePartner._id
            );
          });

          this.bills[
            `s-${this.buyNowPayLaterTransactionsReverse[i].store._id}-p-${this.buyNowPayLaterTransactionsReverse[i].buyNowPayLater.liablePartner._id}`
          ] = {
            store: this.stores[storeIndex],
            partner: this.partners[partnerIndex],
            transactions: [this.buyNowPayLaterTransactionsReverse[i]],
            totalBilletvalue: this.buyNowPayLaterTransactionsReverse[i].details
              .paymentMethodValue
          };
        } else {
          this.bills[
            `s-${this.buyNowPayLaterTransactionsReverse[i].store._id}-p-${this.buyNowPayLaterTransactionsReverse[i].buyNowPayLater.liablePartner._id}`
          ].transactions.push(this.buyNowPayLaterTransactionsReverse[i]);

          this.bills[
            `s-${this.buyNowPayLaterTransactionsReverse[i].store._id}-p-${this.buyNowPayLaterTransactionsReverse[i].buyNowPayLater.liablePartner._id}`
          ].totalBilletvalue += this.buyNowPayLaterTransactionsReverse[
            i
          ].details.paymentMethodValue;
        }
      }
    }
  },

  computed: {
    ...mapGetters(["buyNowPayLaterTransactionsReverse", "billetsReverse"])
  },

  mounted() {
    this.getBuyNowPayLater();

    this.$nextTick(() => {
      const tab1 = document.getElementsByClassName("tabs")[0].children[0]
        .children[0];
      tab1.children[0].style.setProperty(
        "color",
        this.$primaryColor,
        "important"
      );
      tab1.children[0].style.setProperty(
        "border-bottom-color",
        this.$primaryColor,
        "important"
      );

      const tabsQty = document.getElementsByClassName("tabs")[0].children[0]
        .children.length;
      for (let i = 1; i < tabsQty; i++) {
        document
          .getElementsByClassName("tabs")[0]
          .children[0].children[i].children[0].style.setProperty(
            "color",
            "4A4A4A",
            "important"
          );
      }
    });
  }
};
</script>

<style scoped>
#buy-now-pay-later-transactions-panel {
  margin-top: 3rem;
  min-height: 94vh;
  padding: 2rem 1rem;
  background-color: hsl(0, 0%, 96%);
}

.separator {
  background-color: #e5e5e5;
}
</style>
